import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { TronGrid } from "../components/TronGrid";


const FONT_URL =
  "https://fonts.googleapis.com/css2?family=Press+Start+2P&family=Space+Mono:wght@400;700&display=swap";
const BLUE = "#006AA7";
const YELLOW = "#FECC00";
const DARK_BG = "#000a12";
const PIXEL = "'Press Start 2P', monospace";
const MONO = "'Space Mono', 'Courier New', monospace";

function useTyping(text: string, speed = 50, delay = 800) {
  const [out, setOut] = useState("");
  useEffect(() => {
    let i = 0;
    const t = setTimeout(() => {
      const iv = setInterval(() => {
        setOut(text.slice(0, ++i));
        if (i >= text.length) clearInterval(iv);
      }, speed);
    }, delay);
    return () => clearTimeout(t);
  }, [text, speed, delay]);
  return out;
}

const FEATS = [
  { k: "ISOLATION", v: "Firecracker microVM per agent. Zero shared surface." },
  { k: "SPEED", v: "Sub-second cold start. Faster than you can blink." },
  { k: "IDENTITY", v: "Blockchain-anchored agent IDs. Verifiable on-chain." },
  { k: "VAULT", v: "AES-256-GCM encrypted credentials. Zero-knowledge." },
  { k: "PERSONAS", v: "MBTI-driven AI personalities. Forged in the Hatchery." },
  { k: "MONITOR", v: "Prometheus + Grafana. Full observability stack." },
];

const WHY_ITEMS = [
  {
    title: "INJECTION-PROOF BY DESIGN",
    desc: "Every AI agent runs inside its own Firecracker microVM — a hardware-enforced sandbox. Prompt injection, data leakage, and cross-tenant attacks are physically impossible. Not filtered. Not blocked. Impossible.",
  },
  {
    title: "BLOCKCHAIN-VERIFIED EXECUTION",
    desc: "Every agent action is cryptographically signed and anchored on-chain. Immutable audit trails mean you can prove exactly what your agent did, when, and why. No trust required — only math.",
  },
  {
    title: "SWEDISH JURISDICTION",
    desc: "Hosted in Sweden under EU law. GDPR-native. No US Cloud Act exposure. No backdoors. Your data stays in the Nordics and is protected by some of the strongest privacy legislation on Earth.",
  },
  {
    title: "SUB-SECOND COLD STARTS",
    desc: "Firecracker VMs spin up in under one second. Your agents go from zero to running faster than a container, with the isolation of a full virtual machine. Scale to thousands without breaking a sweat.",
  },
];

const PLATFORM_ITEMS = [
  {
    icon: "🥚",
    title: "THE HATCHERY",
    desc: "Birth AI agents with unique MBTI-driven personalities. Configure traits, assign skills, and watch them come alive — each one distinct, each one verifiable.",
  },
  {
    icon: "🔐",
    title: "CREDENTIAL VAULT",
    desc: "AES-256-GCM encrypted secret storage. Inject API keys, tokens, and credentials into agent VMs at runtime. Zero-knowledge architecture — even we can't read your secrets.",
  },
  {
    icon: "🧩",
    title: "MOLTHUB SKILLS",
    desc: "A marketplace of composable agent skills. Plug in web scraping, social media, code execution, data analysis, and more. Build once, reuse everywhere.",
  },
  {
    icon: "📡",
    title: "REAL-TIME OBSERVABILITY",
    desc: "Full Prometheus + Grafana stack. Monitor CPU, memory, network per agent. Stream logs in real-time. Set alerts. Debug live. Nothing is a black box.",
  },
  {
    icon: "⛓️",
    title: "ON-CHAIN IDENTITY",
    desc: "Every agent gets a blockchain-anchored ID. Verify agent authenticity, trace action history, and build trust with cryptographic proof — not promises.",
  },
  {
    icon: "🌐",
    title: "API-FIRST",
    desc: "Full REST API, Python SDK, and CLI. Automate everything. Deploy agents programmatically. Integrate GridHeimr into your existing CI/CD, orchestration, or custom tooling.",
  },
];

export function LandingPage() {
  const nav = useNavigate();
  const typed = useTyping("Deploy autonomous AI in hardware-isolated microVMs.");
  const fRef = useRef(null);
  const fVis = useInView(fRef, { once: true, margin: "-80px" });
  const whyRef = useRef(null);
  const whyVis = useInView(whyRef, { once: true, margin: "-80px" });
  const platRef = useRef(null);
  const platVis = useInView(platRef, { once: true, margin: "-80px" });
  const ossRef = useRef(null);
  const ossVis = useInView(ossRef, { once: true, margin: "-80px" });
  const entRef = useRef(null);
  const entVis = useInView(entRef, { once: true, margin: "-80px" });
  const ctRef = useRef(null);
  const ctVis = useInView(ctRef, { once: true, margin: "-80px" });
  const [formSent, setFormSent] = useState(false);



  useEffect(() => {
    if (!document.getElementById("gh-fonts")) {
      const l = Object.assign(document.createElement("link"), {
        id: "gh-fonts", rel: "stylesheet", href: FONT_URL,
      });
      document.head.appendChild(l);
    }
    if (!document.getElementById("gh-css")) {
      const s = Object.assign(document.createElement("style"), { id: "gh-css" });
      s.textContent = `
        @keyframes gh-blink{0%,100%{opacity:1}50%{opacity:0}}
        @keyframes gh-pulse{0%,100%{box-shadow:0 0 8px ${BLUE},0 0 20px rgba(0,106,167,0.3)}50%{box-shadow:0 0 16px ${BLUE},0 0 40px rgba(0,106,167,0.5)}}
        .gh-cur{display:inline-block;width:10px;height:1.1em;
          background:${YELLOW};margin-left:4px;vertical-align:text-bottom;
          animation:gh-blink 1s step-end infinite;
          box-shadow:0 0 8px ${YELLOW},0 0 16px rgba(254,204,0,0.4)}
        .gh-btn:hover{transform:translate(-2px,-2px);
          box-shadow:0 0 20px ${YELLOW},0 0 40px rgba(254,204,0,0.3),6px 6px 0 ${BLUE} !important}
        .gh-btn2:hover{background:rgba(0,106,167,0.15) !important;
          box-shadow:0 0 20px rgba(0,106,167,0.4) !important}
        .gh-fcard:hover{border-color:${YELLOW} !important;
          box-shadow:0 0 30px rgba(254,204,0,0.12),inset 0 0 30px rgba(254,204,0,0.03)}
        .gh-flink:hover{color:${BLUE} !important;text-shadow:0 0 8px rgba(0,106,167,0.6)}
        .gh-comp:hover{color:${YELLOW} !important;border-color:${YELLOW} !important;
          box-shadow:0 0 15px rgba(254,204,0,0.2)}
        .gh-contact-form input:focus,.gh-contact-form textarea:focus{
          border-color:${BLUE} !important;
          box-shadow:0 0 12px rgba(0,106,167,0.3) !important;
          background:rgba(0,106,167,0.1) !important}
        .gh-contact-form input::placeholder,.gh-contact-form textarea::placeholder{
          color:rgba(255,255,255,0.25)}
        @media(max-width:768px){
          .gh-why-grid{grid-template-columns:1fr !important}
          .gh-fcard{padding:1.8rem !important}
        }
        @media(max-width:640px){
          .gh-fgrid{grid-template-columns:1fr !important}
          .gh-plat-grid{grid-template-columns:1fr !important}
          .gh-ent-grid{grid-template-columns:1fr !important}
          .gh-why-grid{grid-template-columns:1fr !important}
          .gh-comp-grid{gap:1rem !important}
          .gh-comp-item{font-size:0.5rem !important;padding:10px 16px !important}
          .gh-cta{flex-direction:column !important;align-items:center !important}
          .gh-btn,.gh-btn2{width:100% !important;max-width:280px !important;text-align:center !important}
          .gh-form-row{grid-template-columns:1fr !important}
          .gh-contact-form button{align-self:stretch !important}
          .gh-fcard{padding:1.5rem !important}
          .gh-section{padding-top:4rem !important;padding-bottom:5rem !important}
          .gh-oss-item{padding:1.2rem !important}
        }
        @media(max-width:400px){
          .gh-fcard{padding:1.2rem !important}
          .gh-section{padding-top:3rem !important;padding-bottom:4rem !important}
          .gh-oss-item{padding:1rem !important}
          .gh-btn,.gh-btn2{padding:14px 24px !important;font-size:0.5rem !important}
          .gh-contact-form input,.gh-contact-form textarea{padding:10px 12px !important;font-size:0.85rem !important}
        }
        @media(max-height:500px){
          .gh-hero-inner{padding-top:20vh !important}
        }
      `;
      document.head.appendChild(s);
    }
  }, []);

  return (
    <div style={S.page}>

      {/* ─── HERO ─── */}
      <section style={S.hero}>
        <TronGrid />

        {/* Main content — sits in the upper portion above the grid */}
        <motion.div className="gh-hero-inner" style={S.inner}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ duration: 1 }}>

          <motion.h1 style={S.h1}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}>
            <span style={S.h1Blue}>GRID</span>
            <span style={S.h1Yellow}>HEIMR</span>
          </motion.h1>

          <div style={S.typedWrap}>
            <span style={S.typed}>{typed}</span>
            <span className="gh-cur" />
          </div>

          <motion.div className="gh-cta" style={S.cta}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.6 }}>
            <button className="gh-btn" style={S.btn1}
              onClick={() => nav("/register")}>ENTER THE GRID</button>
            <button className="gh-btn2" style={S.btn2}
              onClick={() => nav("/login")}>SIGN IN</button>
          </motion.div>
        </motion.div>

        {/* Subtitle at the horizon — bottom of the hero / vanishing point of the grid */}
        <motion.p style={S.horizonSub}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.5, duration: 1.5 }}>
          Swedish tech · Secure and private by default
        </motion.p>
      </section>

      {/* ─── FEATURES ─── */}
      <section className="gh-section" style={S.feats} ref={fRef}>
        <motion.p style={S.fLabel}
          initial={{ opacity: 0 }}
          animate={fVis ? { opacity: 0.3 } : {}}
          transition={{ duration: 0.6 }}>
          {"// CAPABILITIES"}
        </motion.p>
        <div className="gh-fgrid" style={S.fGrid}>
          {FEATS.map((f, i) => (
            <motion.div key={f.k} className="gh-fcard" style={S.fCard}
              initial={{ opacity: 0, y: 30 }}
              animate={fVis ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: i * 0.08 }}>
              <div style={S.fK}>{f.k}</div>
              <div style={S.fV}>{f.v}</div>
            </motion.div>
          ))}
        </div>

      </section>

      {/* ─── WHY GRIDHEIMR ─── */}
      <section className="gh-section" style={S.whySection} ref={whyRef}>
        <motion.p style={S.fLabel}
          initial={{ opacity: 0 }}
          animate={whyVis ? { opacity: 0.3 } : {}}
          transition={{ duration: 0.6 }}>
          {"// WHY GRIDHEIMR"}
        </motion.p>
        <motion.h2 style={S.sectionH2}
          initial={{ opacity: 0, y: 20 }}
          animate={whyVis ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}>
          Security isn't a feature.<br />It's the architecture.
        </motion.h2>
        <motion.p style={S.sectionSub}
          initial={{ opacity: 0 }}
          animate={whyVis ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}>
          Most platforms bolt security on after the fact. We built the entire stack around
          hardware-level isolation. Every agent is a fortress.
        </motion.p>
        <div className="gh-why-grid" style={S.whyGrid}>
          {WHY_ITEMS.map((item, i) => (
            <motion.div key={item.title} className="gh-fcard" style={S.whyCard}
              initial={{ opacity: 0, y: 30 }}
              animate={whyVis ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.1 }}>
              <div style={S.fK}>{item.title}</div>
              <div style={S.fV}>{item.desc}</div>
            </motion.div>
          ))}
        </div>

      </section>

      {/* ─── THE PLATFORM ─── */}
      <section className="gh-section" style={S.platformSection} ref={platRef}>
        <motion.p style={S.fLabel}
          initial={{ opacity: 0 }}
          animate={platVis ? { opacity: 0.3 } : {}}
          transition={{ duration: 0.6 }}>
          {"// THE PLATFORM"}
        </motion.p>
        <motion.h2 style={S.sectionH2}
          initial={{ opacity: 0, y: 20 }}
          animate={platVis ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}>
          Everything you need.<br />Nothing you don't.
        </motion.h2>
        <div className="gh-plat-grid" style={S.platGrid}>
          {PLATFORM_ITEMS.map((item, i) => (
            <motion.div key={item.title} className="gh-fcard" style={S.platCard}
              initial={{ opacity: 0, y: 30 }}
              animate={platVis ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.08 }}>
              <div style={S.platIcon}>{item.icon}</div>
              <div style={S.fK}>{item.title}</div>
              <div style={S.fV}>{item.desc}</div>
            </motion.div>
          ))}
        </div>

      </section>

      {/* ─── OPEN SOURCE ─── */}
      <section className="gh-section" style={S.ossSection} ref={ossRef}>
        <motion.p style={S.fLabel}
          initial={{ opacity: 0 }}
          animate={ossVis ? { opacity: 0.3 } : {}}
          transition={{ duration: 0.6 }}>
          {"// OPEN SOURCE"}
        </motion.p>
        <motion.h2 style={S.sectionH2}
          initial={{ opacity: 0, y: 20 }}
          animate={ossVis ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}>
          Built in the open.<br />Owned by everyone.
        </motion.h2>
        <motion.p style={S.sectionSub}
          initial={{ opacity: 0 }}
          animate={ossVis ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}>
          GridHeimr's core is open source. Fork it, extend it, audit it, self-host it.
          We believe the best infrastructure is transparent infrastructure.
        </motion.p>
        <div style={S.ossPoints}>
          {[
            { k: "FULL SOURCE", v: "The entire orchestration layer, SDK, and CLI are open. No proprietary black boxes." },
            { k: "COMMUNITY SKILLS", v: "Build and share agent skills on MoltHub. The community decides what matters." },
            { k: "SELF-HOST OPTION", v: "Run the full stack on your own infrastructure. Docker Compose and you're live." },
          ].map((item, i) => (
            <motion.div key={item.k} className="gh-oss-item" style={S.ossItem}
              initial={{ opacity: 0, x: -20 }}
              animate={ossVis ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}>
              <span style={S.ossBullet}>▸</span>
              <div>
                <span style={S.fK}>{item.k}</span>
                <p style={{ ...S.fV, marginTop: "0.5rem" }}>{item.v}</p>
              </div>
            </motion.div>
          ))}
        </div>

      </section>

      {/* ─── ENTERPRISE ─── */}
      <section className="gh-section" style={S.entSection} ref={entRef}>
        <motion.p style={S.fLabel}
          initial={{ opacity: 0 }}
          animate={entVis ? { opacity: 0.3 } : {}}
          transition={{ duration: 0.6 }}>
          {"// ENTERPRISE"}
        </motion.p>
        <motion.h2 style={S.sectionH2}
          initial={{ opacity: 0, y: 20 }}
          animate={entVis ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}>
          Built for teams that<br />can't afford to compromise.
        </motion.h2>
        <motion.p style={S.sectionSub}
          initial={{ opacity: 0 }}
          animate={entVis ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}>
          From startups deploying their first agent to enterprises running thousands —
          GridHeimr scales with you. Dedicated support, SLAs, and custom deployments available.
        </motion.p>
        <div className="gh-ent-grid" style={S.entGrid}>
          {[
            { title: "DEDICATED CLUSTERS", desc: "Isolated compute pools for your organization. No noisy neighbors. No shared resources." },
            { title: "SSO & RBAC", desc: "SAML/OIDC single sign-on. Role-based access control. Audit logging for every action." },
            { title: "CUSTOM SLAs", desc: "99.9%+ uptime guarantees. Dedicated support channels. Incident response commitments." },
            { title: "ON-PREM DEPLOYMENT", desc: "Run GridHeimr behind your firewall. Air-gapped environments supported. Full data sovereignty." },
          ].map((item, i) => (
            <motion.div key={item.title} className="gh-fcard" style={S.entCard}
              initial={{ opacity: 0, y: 30 }}
              animate={entVis ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.1 }}>
              <div style={S.fK}>{item.title}</div>
              <div style={S.fV}>{item.desc}</div>
            </motion.div>
          ))}
        </div>

      </section>

      {/* ─── RUNNING ON GRIDHEIMR ─── */}
      <section style={S.companies}>
        <motion.p style={S.compLabel}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.3 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}>
          {"// RUNNING ON GRIDHEIMR"}
        </motion.p>
        <motion.div className="gh-comp-grid" style={S.compGrid}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}>
          {[
            { name: "Indiependr.ai", href: "https://indiependr.ai" },
            { name: "SE100.xyz", href: "https://www.se100.xyz" },
            { name: "VIRALR.ai", href: "https://viralr.ai" },
            { name: "EXITR.tech", href: "https://exitr.tech" },
          ].map((c) => (
            <a key={c.name} href={c.href} target="_blank" rel="noopener noreferrer"
              className="gh-comp gh-comp-item" style={S.compItem}>
              {c.name}
            </a>
          ))}
        </motion.div>
      </section>

      {/* ─── CONTACT ─── */}
      <section className="gh-section" style={S.contactSection} ref={ctRef}>
        <motion.p style={S.fLabel}
          initial={{ opacity: 0 }}
          animate={ctVis ? { opacity: 0.3 } : {}}
          transition={{ duration: 0.6 }}>
          {"// CONTACT"}
        </motion.p>
        <motion.h2 style={S.sectionH2}
          initial={{ opacity: 0, y: 20 }}
          animate={ctVis ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}>
          Get in touch.
        </motion.h2>
        <motion.p style={S.sectionSub}
          initial={{ opacity: 0 }}
          animate={ctVis ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}>
          Whether you're exploring GridHeimr for your team, want to contribute to the open source
          project, or just want to say hello — we'd love to hear from you.
        </motion.p>

        {formSent ? (
          <motion.div style={S.formSuccess}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}>
            <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>✓</div>
            <div style={S.fK}>MESSAGE SENT</div>
            <p style={{ ...S.fV, marginTop: "0.5rem" }}>
              We'll get back to you shortly.
            </p>
          </motion.div>
        ) : (
          <motion.form
            name="contact"
            method="POST"
            data-netlify="true"
            netlify-honeypot="bot-field"
            className="gh-contact-form"
            style={S.contactForm}
            initial={{ opacity: 0, y: 20 }}
            animate={ctVis ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              fetch("/", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: new URLSearchParams(new FormData(form) as unknown as Record<string, string>).toString(),
              }).then(() => setFormSent(true)).catch(() => setFormSent(true));
            }}>
            <input type="hidden" name="form-name" value="contact" />
            <p style={{ display: "none" }}>
              <label>Don't fill this out: <input name="bot-field" /></label>
            </p>
            <div className="gh-form-row" style={S.formRow}>
              <div style={S.formField}>
                <label style={S.formLabel}>NAME</label>
                <input type="text" name="name" required style={S.formInput}
                  placeholder="Your name" />
              </div>
              <div style={S.formField}>
                <label style={S.formLabel}>EMAIL</label>
                <input type="email" name="email" required style={S.formInput}
                  placeholder="you@company.com" />
              </div>
            </div>
            <div style={S.formField}>
              <label style={S.formLabel}>SUBJECT</label>
              <input type="text" name="subject" style={S.formInput}
                placeholder="Enterprise inquiry, partnership, feedback..." />
            </div>
            <div style={S.formField}>
              <label style={S.formLabel}>MESSAGE</label>
              <textarea name="message" required rows={5} style={S.formTextarea}
                placeholder="Tell us what you're building..." />
            </div>
            <button type="submit" className="gh-btn" style={S.formBtn}>
              SEND MESSAGE
            </button>
          </motion.form>
        )}

        <motion.div style={S.contactInfo}
          initial={{ opacity: 0 }}
          animate={ctVis ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}>
          <a href="https://heimlandr.io" target="_blank" rel="noopener noreferrer"
            style={S.contactLink}>HEIMLANDR.io</a>
          <span style={S.contactDot}>·</span>
          <span style={S.contactMeta}>Stockholm, Sweden</span>
          <span style={S.contactDot}>·</span>
          <a href="https://github.com/HEIMLANDR-ORGANIZATION" target="_blank" rel="noopener noreferrer"
            style={S.contactLink}>GitHub</a>
        </motion.div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer style={S.foot}>
        <div style={S.footLine} />
        <p style={S.footMain}>Swedish Tech · Secure & Private by Default</p>
        <p style={S.footSub}>
          Initiative by{" "}
          <a href="https://heimlandr.io" target="_blank" rel="noopener noreferrer"
            style={S.footHL}>HEIMLANDR.io</a>
          {" · Developed by the in-house "}
          <span style={S.footPR}>PunkRockTech</span> team
        </p>
        <div style={S.footNav}>
          <a href="/lab" className="gh-flink" style={S.footNL}>Lab</a>
          <span style={S.footDot}>·</span>
          <a href="/login" className="gh-flink" style={S.footNL}>Login</a>
          <span style={S.footDot}>·</span>
          <a href="/register" className="gh-flink" style={S.footNL}>Register</a>
        </div>
      </footer>
    </div>
  );
}
/* ═══ STYLES — Swedish Tron ═══ */
const S: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: DARK_BG,
    color: "#fff",
    fontFamily: MONO,
    overflowX: "hidden",
  },
  hero: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    minHeight: "100vh",
    padding: "2rem",
    overflow: "hidden",
  },
  inner: {
    position: "relative",
    zIndex: 2,
    textAlign: "center",
    maxWidth: "860px",
    width: "100%",
    paddingTop: "38vh",
  },
  horizonSub: {
    position: "absolute",
    bottom: "8%",
    left: 0,
    right: 0,
    zIndex: 2,
    textAlign: "center",
    fontFamily: MONO,
    fontSize: "clamp(0.9rem, 1.8vw, 1.2rem)",
    color: "rgba(255,255,255,0.55)",
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    textShadow: `0 0 15px rgba(0,106,167,0.5), 0 0 40px rgba(0,106,167,0.2)`,
  },

  h1: {
    fontFamily: PIXEL,
    fontSize: "clamp(1.8rem, 6vw, 4rem)",
    fontWeight: 400,
    lineHeight: 1.1,
    margin: "0 0 2.5rem",
    letterSpacing: "0.08em",
  },
  h1Blue: {
    color: BLUE,
    textShadow: `0 0 20px rgba(0,106,167,0.6), 0 0 60px rgba(0,106,167,0.3)`,
  },
  h1Yellow: {
    color: YELLOW,
    textShadow: `0 0 20px rgba(254,204,0,0.5), 0 0 60px rgba(254,204,0,0.2)`,
  },
  typedWrap: { minHeight: "2rem", marginBottom: "1.5rem" },
  typed: {
    fontFamily: MONO,
    fontSize: "clamp(1rem, 2.5vw, 1.35rem)",
    color: "rgba(255,255,255,0.9)",
  },

  cta: {
    display: "flex",
    gap: "1.5rem",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  btn1: {
    fontFamily: PIXEL,
    fontSize: "0.55rem",
    fontWeight: 400,
    padding: "18px 36px",
    background: YELLOW,
    color: "#000",
    border: "none",
    cursor: "pointer",
    letterSpacing: "0.1em",
    transition: "all 0.15s ease",
    boxShadow: `0 0 12px rgba(254,204,0,0.4), 4px 4px 0 ${BLUE}`,
  },
  btn2: {
    fontFamily: PIXEL,
    fontSize: "0.55rem",
    fontWeight: 400,
    padding: "18px 36px",
    background: "transparent",
    color: BLUE,
    border: `2px solid ${BLUE}`,
    cursor: "pointer",
    letterSpacing: "0.1em",
    transition: "all 0.15s ease",
    boxShadow: `0 0 8px rgba(0,106,167,0.2)`,
  },
  feats: {
    padding: "6rem 2rem 8rem",
    maxWidth: "1100px",
    margin: "0 auto",
    position: "relative",
  },
  fLabel: {
    fontFamily: MONO,
    fontSize: "0.75rem",
    color: "rgba(255,255,255,0.55)",
    letterSpacing: "0.15em",
    marginBottom: "3rem",
  },
  fGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "1px",
    background: `rgba(0,106,167,0.08)`,
    border: `1px solid rgba(0,106,167,0.15)`,
    boxShadow: `0 0 30px rgba(0,106,167,0.05)`,
  },
  fCard: {
    background: DARK_BG,
    padding: "2.5rem",
    transition: "all 0.3s ease",
    borderColor: "transparent",
    borderWidth: 1,
    borderStyle: "solid",
  },
  fK: {
    fontFamily: PIXEL,
    fontSize: "0.55rem",
    fontWeight: 400,
    color: YELLOW,
    marginBottom: "1rem",
    letterSpacing: "0.15em",
    textShadow: `0 0 10px rgba(254,204,0,0.4)`,
  },
  fV: {
    fontFamily: MONO,
    fontSize: "0.95rem",
    color: "rgba(255,255,255,0.75)",
    lineHeight: 1.7,
  },
  /* ── Shared section styles ── */
  sectionH2: {
    fontFamily: PIXEL,
    fontSize: "clamp(0.7rem, 2vw, 1rem)",
    fontWeight: 400,
    color: "#fff",
    lineHeight: 1.8,
    marginBottom: "1.5rem",
    letterSpacing: "0.06em",
    textShadow: `0 0 15px rgba(0,106,167,0.4)`,
  },
  sectionSub: {
    fontFamily: MONO,
    fontSize: "clamp(0.85rem, 1.5vw, 1.05rem)",
    color: "rgba(255,255,255,0.6)",
    lineHeight: 1.8,
    maxWidth: "680px",
    marginBottom: "3rem",
  },

  /* ── Why GridHeimr ── */
  whySection: {
    padding: "6rem 2rem 8rem",
    maxWidth: "1100px",
    margin: "0 auto",
    position: "relative",
    overflow: "hidden",
  },
  whyGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(480px, 1fr))",
    gap: "1px",
    background: `rgba(0,106,167,0.08)`,
    border: `1px solid rgba(0,106,167,0.15)`,
  },
  whyCard: {
    background: DARK_BG,
    padding: "2.5rem",
    transition: "all 0.3s ease",
    borderColor: "transparent",
    borderWidth: 1,
    borderStyle: "solid",
  },

  /* ── Platform ── */
  platformSection: {
    padding: "6rem 2rem 8rem",
    maxWidth: "1100px",
    margin: "0 auto",
    position: "relative",
    overflow: "hidden",
  },
  platGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: "1px",
    background: `rgba(0,106,167,0.08)`,
    border: `1px solid rgba(0,106,167,0.15)`,
  },
  platCard: {
    background: DARK_BG,
    padding: "2.5rem",
    transition: "all 0.3s ease",
    borderColor: "transparent",
    borderWidth: 1,
    borderStyle: "solid",
  },
  platIcon: {
    fontSize: "1.8rem",
    marginBottom: "1rem",
  },

  /* ── Open Source ── */
  ossSection: {
    padding: "6rem 2rem 8rem",
    maxWidth: "900px",
    margin: "0 auto",
    position: "relative",
    overflow: "hidden",
  },
  ossPoints: {
    display: "flex",
    flexDirection: "column",
    gap: "2rem",
  },
  ossItem: {
    display: "flex",
    gap: "1rem",
    alignItems: "flex-start",
    padding: "2rem",
    border: `1px solid rgba(0,106,167,0.12)`,
    background: `rgba(0,106,167,0.03)`,
    transition: "all 0.3s ease",
  },
  ossBullet: {
    color: YELLOW,
    fontSize: "1.2rem",
    lineHeight: 1,
    marginTop: "0.2rem",
    textShadow: `0 0 8px rgba(254,204,0,0.4)`,
  },

  /* ── Enterprise ── */
  entSection: {
    padding: "6rem 2rem 8rem",
    maxWidth: "1100px",
    margin: "0 auto",
    position: "relative",
    overflow: "hidden",
    borderTop: `1px solid rgba(0,106,167,0.1)`,
  },
  entGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "1px",
    background: `rgba(254,204,0,0.05)`,
    border: `1px solid rgba(254,204,0,0.12)`,
  },
  entCard: {
    background: DARK_BG,
    padding: "2.5rem",
    transition: "all 0.3s ease",
    borderColor: "transparent",
    borderWidth: 1,
    borderStyle: "solid",
  },


  /* ── Companies ── */
  companies: {
    padding: "4rem 2rem 6rem",
    maxWidth: "900px",
    margin: "0 auto",
    textAlign: "center",
  },
  compLabel: {
    fontFamily: MONO,
    fontSize: "0.75rem",
    color: "rgba(255,255,255,0.55)",
    letterSpacing: "0.15em",
    marginBottom: "2.5rem",
  },
  compGrid: {
    display: "flex",
    justifyContent: "center",
    gap: "2rem",
    flexWrap: "wrap",
    marginBottom: "2.5rem",
  },
  compItem: {
    fontFamily: PIXEL,
    fontSize: "0.5rem",
    fontWeight: 400,
    color: "rgba(255,255,255,0.7)",
    textDecoration: "none",
    letterSpacing: "0.1em",
    padding: "12px 24px",
    border: `1px solid rgba(0,106,167,0.2)`,
    transition: "all 0.2s ease",
    boxShadow: `0 0 8px rgba(0,106,167,0.1)`,
  },
  /* ── Contact ── */
  contactSection: {
    padding: "6rem 2rem 8rem",
    maxWidth: "700px",
    margin: "0 auto",
    position: "relative",
    borderTop: `1px solid rgba(0,106,167,0.1)`,
  },
  contactForm: {
    display: "flex",
    flexDirection: "column",
    gap: "1.5rem",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "1.5rem",
  },
  formField: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  },
  formLabel: {
    fontFamily: PIXEL,
    fontSize: "0.45rem",
    fontWeight: 400,
    color: YELLOW,
    letterSpacing: "0.15em",
    textShadow: `0 0 8px rgba(254,204,0,0.3)`,
  },
  formInput: {
    fontFamily: MONO,
    fontSize: "0.9rem",
    color: "#fff",
    background: "rgba(0,106,167,0.06)",
    border: `1px solid rgba(0,106,167,0.2)`,
    padding: "12px 16px",
    outline: "none",
    transition: "all 0.2s ease",
  },
  formTextarea: {
    fontFamily: MONO,
    fontSize: "0.9rem",
    color: "#fff",
    background: "rgba(0,106,167,0.06)",
    border: `1px solid rgba(0,106,167,0.2)`,
    padding: "12px 16px",
    outline: "none",
    resize: "vertical" as const,
    minHeight: "120px",
    transition: "all 0.2s ease",
  },
  formBtn: {
    fontFamily: PIXEL,
    fontSize: "0.5rem",
    fontWeight: 400,
    color: DARK_BG,
    background: YELLOW,
    border: `2px solid ${YELLOW}`,
    padding: "16px 32px",
    cursor: "pointer",
    letterSpacing: "0.12em",
    alignSelf: "flex-start",
    transition: "all 0.2s ease",
    boxShadow: `0 0 12px rgba(254,204,0,0.3)`,
  },
  formSuccess: {
    textAlign: "center",
    padding: "3rem 2rem",
    border: `1px solid rgba(0,106,167,0.2)`,
    background: "rgba(0,106,167,0.04)",
  },
  contactInfo: {
    marginTop: "3rem",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "0.8rem",
    flexWrap: "wrap",
  },
  contactLink: {
    fontFamily: PIXEL,
    fontSize: "0.45rem",
    fontWeight: 400,
    color: YELLOW,
    textDecoration: "none",
    letterSpacing: "0.1em",
    textShadow: `0 0 8px rgba(254,204,0,0.3)`,
  },
  contactDot: {
    color: "rgba(255,255,255,0.3)",
  },
  contactMeta: {
    fontFamily: MONO,
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.5)",
  },

  /* ── Footer ── */
  foot: { padding: "4rem 2rem 3rem", textAlign: "center" },
  footLine: {
    width: "60px",
    height: "2px",
    background: BLUE,
    margin: "0 auto 3rem",
    boxShadow: `0 0 10px ${BLUE}`,
  },
  footMain: {
    fontFamily: MONO,
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.65)",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginBottom: "1rem",
  },
  footSub: {
    fontFamily: MONO,
    fontSize: "0.75rem",
    color: "rgba(255,255,255,0.5)",
    marginBottom: "2rem",
  },
  footHL: {
    color: YELLOW,
    textDecoration: "none",
    fontWeight: 700,
    textShadow: `0 0 8px rgba(254,204,0,0.3)`,
  },
  footPR: {
    color: YELLOW,
    fontStyle: "italic",
    fontWeight: 700,
    textShadow: `0 0 8px rgba(254,204,0,0.3)`,
  },
  footNav: { display: "flex", justifyContent: "center", gap: "1rem" },
  footNL: {
    fontFamily: MONO,
    fontSize: "0.7rem",
    color: "rgba(255,255,255,0.45)",
    textDecoration: "none",
    letterSpacing: "0.1em",
    transition: "color 0.2s",
  },
  footDot: { color: "rgba(255,255,255,0.3)" },
};
